import type { CampaignBudgetChange } from "@adpilot/shared";
import { IconChartLine } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchCampaignBudgetHistory } from "@/lib/campaign-api";
import { useFormatters } from "@/lib/format";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

/**
 * ADP-222: 「预算变化趋势图」— the daily budget in force per marketplace day, and
 * the changes behind it. Days before AdPilot began recording stay blank: the
 * current budget says nothing about them.
 */
export function CampaignBudgetHistoryCard({
  campaignId,
  campaignType,
}: {
  campaignId: string;
  campaignType: string;
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatDate, locale } = useFormatters();
  // Budgets are only recorded where AdPilot sees them change: the SP settings
  // editor and the SP structure sync. SB/SD have neither yet.
  const supported = campaignType === "sp";
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  // Key starts with ["campaign", id] so a budget edit's invalidation refreshes it.
  const query = useQuery({
    queryKey: ["campaign", campaignId, "budget-history", range, timeZone],
    queryFn: () => fetchCampaignBudgetHistory(campaignId, range, timeZone),
    enabled: supported,
  });
  const money = (value: number) => formatCurrency(value, query.data?.currency ?? "USD");
  const chartConfig = {
    // Same series colour as the Campaign trend chart above it.
    dailyBudget: {
      label: t("detail.budget.history.series"),
      color: "var(--sales-chart-primary, #679bff)",
    },
  } satisfies ChartConfig;

  const trackedSince = query.data?.trackedSince ?? null;
  const lastDay = query.data?.points.at(-1)?.date ?? null;
  const hasDailyBudget = query.data?.points.some((point) => point.dailyBudget != null) ?? false;
  // Change times use the zone the chart's days are cut in, so a change and its
  // step always land on the same date (the app default zone would not).
  const changeTime = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: query.data?.timeZone ?? "UTC",
      timeZoneName: "short",
    }).format(new Date(iso));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.budget.history.title")}</CardTitle>
        <CardDescription>
          {trackedSince
            ? t("detail.budget.history.trackedSince", { date: formatDate(trackedSince) })
            : t("detail.budget.history.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!supported ? (
          <Notice title={t("detail.budget.history.unsupported")} />
        ) : query.isLoading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : query.isError ? (
          <ErrorState
            variant="inline"
            message={t("detail.budget.history.error")}
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
          />
        ) : !trackedSince ? (
          <Notice
            title={t("detail.budget.history.empty")}
            hint={t("detail.budget.history.emptyHint")}
          />
        ) : lastDay && trackedSince > lastDay ? (
          <Notice
            title={t("detail.budget.history.beforeTracking", { date: formatDate(trackedSince) })}
          />
        ) : !hasDailyBudget ? (
          <Notice title={t("detail.budget.history.noDailyBudget")} />
        ) : (
          <div className="flex flex-col gap-4">
            <ChartContainer config={chartConfig} className="h-[220px] w-full">
              <LineChart data={query.data?.points ?? []} margin={{ left: 8, right: 16, top: 8 }}>
                <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(date: string) => date.slice(5)}
                  minTickGap={24}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={64}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value: number) => money(value)}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) =>
                        typeof value === "number"
                          ? money(value)
                          : t("detail.budget.history.noValue")
                      }
                    />
                  }
                />
                {/* A budget holds until it changes: step, don't interpolate. Gaps stay gaps. */}
                <Line
                  dataKey="dailyBudget"
                  type="stepAfter"
                  stroke="var(--color-dailyBudget)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ChartContainer>
            <ChangeList changes={query.data?.changes ?? []} money={money} formatTime={changeTime} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Notice({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex h-[220px] flex-col items-center justify-center gap-2 text-center">
      <IconChartLine className="size-8 text-muted-foreground/50" aria-hidden />
      <p className="text-[13px] font-medium text-ink">{title}</p>
      {hint ? <p className="max-w-sm text-[12px] leading-4 text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function ChangeList({
  changes,
  money,
  formatTime,
}: {
  changes: CampaignBudgetChange[];
  money: (value: number) => string;
  formatTime: (value: string) => string;
}) {
  const { t } = useTranslation("campaign");
  if (changes.length === 0) {
    return (
      <p className="text-[12px] leading-4 text-muted-foreground">
        {t("detail.budget.history.noChanges")}
      </p>
    );
  }
  const amount = (value: number | null, type: string | null) =>
    value != null
      ? money(value)
      : type === "monthly"
        ? t("detail.shell.monthly")
        : t("detail.budget.history.noValue");
  return (
    <ul className="flex flex-col divide-y divide-line text-[12px] leading-4">
      {[...changes].reverse().map((change) => (
        <li key={change.occurredAt} className="flex items-center gap-3 py-2">
          <span className="w-48 shrink-0 font-mono tabular-nums text-muted-foreground">
            {formatTime(change.occurredAt)}
          </span>
          <span className="font-mono tabular-nums text-ink">
            {amount(change.oldDailyBudget, change.oldBudgetType)} →{" "}
            {amount(change.newDailyBudget, change.newBudgetType)}
          </span>
          <Badge variant="neutral" size="compact" className="ml-auto">
            {t(`detail.budget.history.source.${change.source}`)}
          </Badge>
        </li>
      ))}
    </ul>
  );
}
